// Prerendered `/learn` — the learning-first landing page for audio-course and learn-by-listening searches (dfs-gnothi-config.json cluster `series-learn-stories`). Leads with positioning strength 1 ("Your own course, as a podcast") and 2 ("A story that teaches"), docs/gnothi/positioning.md.
// Every claim is code-verified at the same bar as series-usecases.tsx, whose `learn` use case holds the full setup; this page links there rather than restating it. Node-prerender-safe and loader-free (see series.tsx's header); chrome comes from splash-chrome, so this file must NOT wrap `Mui`.
import { Link as RouterLink } from 'react-router';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ChecklistIcon from '@mui/icons-material/ChecklistOutlined';
import TravelExploreIcon from '@mui/icons-material/TravelExploreOutlined';
import BuildIcon from '@mui/icons-material/BuildOutlined';
import EventRepeatIcon from '@mui/icons-material/EventRepeatOutlined';
import { ExplainerVideo } from '../../src/ui/Components/ExplainerVideo';
import { SplashChrome, AUTH_SIGN_UP } from './splash-chrome';
import { metaDescription, splashMeta, softwareApplicationLd, ORIGIN } from './series-ld';
import { Band, CtaBand, Details, FeatureCard, SectionBody, SectionHeader, heroCta } from './splash-sections';
import SeriesDemo from './series-demo';
import type { Route } from './+types/learn';

const TITLE = 'Learn by Listening: AI Audio Courses as a Podcast — Gnothi';
const DESCRIPTION = metaDescription(
  'Name a subject and get a narrated audio course in your podcast app, taught straight or inside a story, with a syllabus that remembers what it covered. Free to start.',
);
const PATH = '/learn';

export const meta: Route.MetaFunction = () => [
  ...splashMeta({ title: TITLE, description: DESCRIPTION, path: PATH }),
  {
    'script:ld+json': softwareApplicationLd({
      name: 'Gnothi audio courses',
      description: DESCRIPTION,
      url: `${ORIGIN}${PATH}`,
      applicationCategory: 'EducationalApplication',
      featureList: [
        'Narrated course chapters delivered to a podcast feed',
        'Editable syllabus that records what each chapter covered',
        'Lessons that can search the web or read a URL you give them',
        'Try It practice segment carried into the next chapter',
        'Straight course or story-based learning on the same subjects',
      ],
    }),
  },
];

function Hero() {
  return (
    <Band color="dark" hero>
      <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center', maxWidth: 820, mx: 'auto' }}>
        <Typography variant="h2" component="h1" sx={{ fontWeight: 700, mb: 0, fontSize: { xs: '2.125rem', sm: '2.5rem', md: '3rem' } }}>
          Your own audio course, in your podcast app
        </Typography>
        {/* Narration is unconditional for every generated chapter (series-features.tsx header); ~31 minutes is the measured length cited in series-features.tsx `podcast-generator` "Full chapters". */}
        <Typography variant="h6" component="p" sx={{ fontWeight: 400, opacity: 0.9 }}>
          Name a subject and Gnothi writes a course on it, one narrated chapter at a time. Each chapter runs about half an hour and shows up in your podcast app, so you learn by listening on a walk, a drive or the dishes.
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.72, maxWidth: 620 }}>
          Take it straight, as a course, or carried inside a story you want to keep hearing. It keeps a syllabus of what it has taught you, so chapter twelve builds on chapter eleven.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 1 }}>
          <Button component={RouterLink} to={AUTH_SIGN_UP} variant="contained" size="large" sx={{ ...heroCta, px: 4 }}>
            Get started free
          </Button>
          <Button href="#how" variant="outlined" color="inherit" size="large">
            How a course works
          </Button>
        </Stack>
        <ExplainerVideo name="stories" sx={{ width: '100%', maxWidth: 720, mx: 'auto', pt: 2 }} />
      </Stack>
    </Band>
  );
}

// Sources, card by card: syllabus tree with covered dates, user-editable (services/data/models/ledger.ts `kind="topic"`) and the refuse-to-circle `<toc>` rule (src/shared/authorship/prompts/needs.mdx:4); per-segment `internet` research with a named URL fetched via Gemini `urlContext`, brief-only egress (src/shared/llm/research.ts); the Try It and Teach Me presets (src/shared/authorship/prompts/presets/segments/); manual or daily/weekly/biweekly/monthly cadence (series-features.tsx `podcast-generator`).
const HOW = [
  { title: 'A syllabus that remembers', icon: <ChecklistIcon />, body: 'Every subject gets a syllabus tree, and each item records the date a chapter covered it. You can read it and edit it to skip ahead or go back, and the next chapter won\'t circle over ground it already taught.' },
  { title: 'Lessons that read the source', icon: <TravelExploreIcon />, body: 'Turn on research for a lesson and it searches the web before writing. Paste a URL into it, such as a syllabus or the docs you work from, and that page is read first. Only the lesson\'s brief goes out with the search.' },
  { title: 'Try It, then the next chapter', icon: <BuildIcon />, body: 'Teach Me shows the next thing to grasp in use. Try It gives you one concrete thing to do before the next chapter. Write a line about how it went, and the chapter after picks it up.' },
  { title: 'On your schedule', icon: <EventRepeatIcon />, body: 'Make the next chapter when you want it, or set a daily, weekly, biweekly or monthly cadence and let new chapters arrive in the feed.' },
];

function How() {
  return (
    <Band id="how" color="light">
      <SectionHeader eyebrow="AI course generator" title="How a course works" deck="Pick a subject, choose segments, subscribe. Rust, sourdough, the Peloponnesian War, Claude Code: subjects like these work well." />
      <Grid container spacing={2}>
        {HOW.map(({ title, icon, body }) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={title}>
            <FeatureCard title={title} icon={icon} tone="prompt">{body}</FeatureCard>
          </Grid>
        ))}
      </Grid>
    </Band>
  );
}

function StoryBased() {
  return (
    <Band id="story" color="grey">
      <SectionHeader eyebrow="Story-based learning" title="A story that teaches" deck="Same subjects, same syllabus. The lessons happen in the plot." />
      {/* Literal-first lesson delivery: src/shared/authorship/prompts/teaching/delivery.mdx. The 60/40 bound: services/ml/buildChapterPartials.ts `ALLOCATED_FIC`. Both as cited in series-usecases.tsx `learn`. */}
      <SectionBody>
        <p>If a straight course isn't what keeps you listening, put the lessons in a world instead: a cyberpunk thriller, a fantasy campaign, a slow-burn mystery. Each lesson is marked literal, adapted or metaphorical before a word is written, and ordinary education leans literal, so a cyberpunk founder learning Claude Code still hears Claude Code named. The lessons can take at most 60% of a chapter, so the story always keeps at least 40%.</p>
        <p>Switch between a story, an analogy and a straight course below. These examples were written in advance, so switching is instant and uses no AI credits.</p>
      </SectionBody>
      <SeriesDemo compact />
    </Band>
  );
}

function Questions() {
  return (
    <Band id="questions" color="light">
      <SectionHeader title="Common questions" />
      {/* Chapter length: series-features.tsx "Full chapters" (~31 minutes). */}
      <Details summary="Is this a microlearning app?">
        <p>Not in the five-minute-card sense. A chapter runs about half an hour and goes deeper than a flashcard can. The microlearning part is the pace: one chapter at a time, on a cadence you set, in the podcast app you already open on a commute.</p>
      </Details>
      {/* Output shape: a titled chapter with teaser and show notes, narrated (series-features.tsx `podcast-generator` "One continuous chapter"). No slide, quiz or LMS export ships. */}
      <Details summary="How is it different from other AI course generators?">
        <p>Most AI course creators build slides and modules for you to hand to a class. Gnothi writes chapters for you to listen to, with a title, teaser and show notes, and it keeps going: the syllabus records what each chapter covered, and what you write between chapters steers the next one.</p>
      </Details>
      {/* Homeschool shape and account model: series-usecases.tsx `learn` homeschool paragraph. */}
      <Details summary="Can I build one for my kid?">
        <p>Yes. Write the term's topics as the subjects and pick a story for them to follow. It runs on your account, and your kid listens through the feed on a speaker or an old phone without a login.</p>
      </Details>
      <SectionBody center>
        <p>The full setup, step by step, is in <RouterLink to="/series#learn">the learning use case</RouterLink>.</p>
      </SectionBody>
    </Band>
  );
}

export default function Learn() {
  return (
    <SplashChrome>
      <Hero />
      <How />
      <StoryBased />
      <Questions />
      <CtaBand
        color="grey"
        title="Pick the subject you keep meaning to learn"
        deck="Name it, choose a course or a story, and subscribe. Making an account is free."
      />
    </SplashChrome>
  );
}
